import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import KpiCard from '@/pages/dashboard/components/KpiCard';
import DashboardAlerts from '@/pages/dashboard/components/DashboardAlerts';
import InsightCards from '@/pages/dashboard/components/InsightCards';
import NewOrdersTable from '@/pages/dashboard/components/NewOrdersTable';
import Toast, { type ToastType } from '@/components/base/Toast';
import { useAppData } from '@/store/AppDataContext';
import {
  dashboardKpis,
  dashboardNewOrders,
  dashboardInsights,
} from '@/mocks/dashboard';
import { liveNotificationEvents } from '@/mocks/liveNotifications';
import LiveNotifications, {
  type LiveNotification,
} from '@/pages/dashboard/components/LiveNotifications';

interface ToastState {
  type: ToastType;
  message: string;
}

const ALERTS_VISIBLE_MS = 7000;
const ALERTS_EXIT_MS = 450;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { alerts } = useAppData();

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState(dashboardNewOrders);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [alertsPhase, setAlertsPhase] = useState<'in' | 'out' | 'hidden'>('in');
  const [liveNotifs, setLiveNotifs] = useState<LiveNotification[]>([]);
  const [flashIds, setFlashIds] = useState<string[]>([]);
  const eventIndexRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const hideTimer = setTimeout(() => setAlertsPhase('out'), ALERTS_VISIBLE_MS);
    return () => clearTimeout(hideTimer);
  }, [loading]);

  useEffect(() => {
    if (alertsPhase !== 'out') return undefined;
    const clearTimer = setTimeout(() => setAlertsPhase('hidden'), ALERTS_EXIT_MS);
    return () => clearTimeout(clearTimer);
  }, [alertsPhase]);

  const handleAccept = (orderNo: string) => {
    setOrders((prev) => prev.filter((order) => order.orderNo !== orderNo));
    setToast({ type: 'success', message: `تم قبول الطلب ${orderNo} وبدأ البحث عن مندوب استلام` });
  };

  const handleReject = (orderNo: string) => {
    setOrders((prev) => prev.filter((order) => order.orderNo !== orderNo));
    setToast({ type: 'info', message: `تم رفض الطلب ${orderNo} وإعادة المبلغ للعميل` });
  };

  const handleAssignFromAlert = (orderNo: string) => {
    const alert = alerts.find((a) => a.orderNo === orderNo);
    const alertId = alert ? alert.id : '';
    navigate(
      `/orders/assign?alertId=${alertId}&orderNo=${encodeURIComponent(orderNo)}&back=/dashboard`
    );
  };

  useEffect(() => {
    if (loading) return undefined;
    let firstTimer: ReturnType<typeof setTimeout>;
    let interval: ReturnType<typeof setInterval>;

    const pushNext = () => {
      const event = liveNotificationEvents[eventIndexRef.current % liveNotificationEvents.length];
      eventIndexRef.current += 1;
      const id = `live-${Date.now()}-${eventIndexRef.current}`;

      setLiveNotifs((prev) => [
        ...prev,
        { id, kind: event.kind, title: event.title, note: event.note, orderNo: event.orderNo },
      ]);

      if (event.kind === 'order') {
        const orderId = `ORD-${event.orderNo.replace('#', '')}`;

        setOrders((prev) => [
          {
            id: orderId,
            orderNo: event.orderNo,
            customer: event.customer ?? '',
            customerPhone: event.customerPhone ?? '',
            items: event.items ?? '',
            itemsCount: event.itemsCount ?? 0,
            priority: event.priority ?? 'normal',
            amount: event.amount ?? '',
            time: 'الآن',
            source: 'مباشر',
          },
          ...prev,
        ]);

        setFlashIds((prev) => [...prev, orderId]);
        setTimeout(() => {
          setFlashIds((prev) => prev.filter((id) => id !== orderId));
        }, 1600);
      }
    };

    firstTimer = setTimeout(pushNext, 2200);
    interval = setInterval(pushNext, 9000);

    return () => {
      clearTimeout(firstTimer);
      clearInterval(interval);
    };
  }, [loading]);

  const handleLiveAction = (n: LiveNotification) => {
    setLiveNotifs((prev) => prev.filter((x) => x.id !== n.id));
    if (n.kind === 'assign') {
      navigate(`/orders/assign?orderNo=${encodeURIComponent(n.orderNo)}&back=/dashboard`);
    } else {
      navigate('/orders');
    }
  };

  const handleLiveDismiss = (id: string) => {
    setLiveNotifs((prev) => prev.filter((x) => x.id !== id));
  };

  const kpis = dashboardKpis.map((kpi) =>
    kpi.id === 'new' ? { ...kpi, value: String(orders.length) } : kpi
  );

  return (
    <DashboardLayout title="الصفحة الرئيسية">
      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}

      <LiveNotifications
        notifications={liveNotifs}
        onDismiss={handleLiveDismiss}
        onAction={handleLiveAction}
      />

      <div className="space-y-6">
        {/* ── live indicator ── */}
        {!loading && (
          <div className="flex items-center justify-end">
            <span className="inline-flex items-center gap-2 rounded-full border border-success-200 bg-success-50 px-3 py-1.5 text-[12px] font-medium text-success-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-500 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-success-500"></span>
              </span>
              تحديثات مباشرة
            </span>
          </div>
        )}

        {/* ── KPI Cards ── */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-[168px] animate-pulse rounded-card border border-background-200 bg-white p-5"
                >
                  <div className="h-11 w-11 rounded-xl bg-background-200" />
                  <div className="mt-4 h-7 w-24 rounded bg-background-200" />
                  <div className="mt-2 h-3 w-20 rounded bg-background-200" />
                  <div className="mt-3 h-1.5 w-full rounded-full bg-background-200" />
                </div>
              ))
            : kpis.map((kpi, i) => (
                <KpiCard
                  key={kpi.id}
                  label={kpi.label}
                  value={kpi.value}
                  icon={kpi.icon}
                  tone={kpi.tone as 'info' | 'primary' | 'success' | 'accent'}
                  note={kpi.note}
                  highlight={kpi.highlight}
                  trend={kpi.trend}
                  delay={i * 75}
                />
              ))}
        </section>

        {/* ── Alerts (تظهر بحركة من تحت ثم تختفي تلقائياً) ── */}
        {!loading && alerts.length > 0 && alertsPhase !== 'hidden' && (
          <div
            className={alertsPhase === 'out' ? 'animate-alert-out' : 'animate-alert-in'}
            style={alertsPhase === 'in' ? { animationDelay: '260ms' } : undefined}
          >
            <DashboardAlerts
              alerts={alerts}
              onAssign={handleAssignFromAlert}
              onOpenOrder={() => navigate('/orders')}
            />
          </div>
        )}

        {/* ── Insight Cards ── */}
        {!loading && (
          <div className="animate-fade-up" style={{ animationDelay: '420ms' }}>
            <div className="mb-3 flex items-center gap-2">
              <h2 className="font-semibold text-base text-foreground-950">نظرة سريعة</h2>
              <span className="text-[12px] text-foreground-400">— إحصاءات اليوم</span>
            </div>
            <InsightCards data={dashboardInsights} delay={440} />
          </div>
        )}

        {/* Insight cards skeleton */}
        {loading && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-[220px] animate-pulse rounded-card border border-background-200 bg-white p-5"
              >
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-background-200" />
                  <div className="h-3 w-24 rounded bg-background-200" />
                </div>
                <div className="mt-4 space-y-2">
                  {Array.from({ length: 4 }).map((__, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-background-200" />
                      <div className="h-1.5 flex-1 rounded-full bg-background-200" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── New Orders Table ── */}
        {loading ? (
          <section>
            <div className="mb-3 h-5 w-32 animate-pulse rounded bg-background-200" />
            <div className="overflow-hidden rounded-card border border-background-200 bg-white">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 border-b border-background-100 px-4 py-4 last:border-0"
                >
                  <div className="h-4 w-16 animate-pulse rounded bg-background-200" />
                  <div className="h-4 w-28 animate-pulse rounded bg-background-200" />
                  <div className="h-4 flex-1 animate-pulse rounded bg-background-200" />
                  <div className="h-7 w-24 animate-pulse rounded-lg bg-background-200" />
                </div>
              ))}
            </div>
          </section>
        ) : orders.length === 0 ? (
          <section className="animate-fade-up" style={{ animationDelay: '500ms' }}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold text-base text-foreground-950">الطلبات الجديدة</h2>
              <button
                type="button"
                onClick={() => navigate('/orders')}
                className="text-[13px] font-medium text-secondary-600 transition-colors hover:text-secondary-700"
              >
                عرض الكل
              </button>
            </div>
            <div className="flex flex-col items-center justify-center rounded-card border border-background-200 bg-white px-6 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-success-50">
                <i className="ri-checkbox-circle-line text-4xl text-success-500" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-semibold text-base text-foreground-950">
                لا توجد طلبات جديدة حالياً
              </h3>
              <p className="mt-1.5 text-[13px] text-foreground-500">
                الطلبات الجديدة هتظهر هنا أول ما توصل
              </p>
            </div>
          </section>
        ) : (
          <div className="animate-fade-up" style={{ animationDelay: '500ms' }}>
            <NewOrdersTable
              orders={orders}
              highlightIds={flashIds}
              onAccept={handleAccept}
              onReject={handleReject}
              onOpenOrder={() => navigate('/orders')}
              onViewAll={() => navigate('/orders')}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}